import { supabase } from '../config/supabase';
import type { MonthlyForecast } from '../types/forecast';
import type { Expense } from '../types/expense';
import type { Subscription } from '../types/subscription';
import { toMonthlyAmount } from './subscriptionService';

// ─── CRUD ────────────────────────────────────────────────────────────────────

export const getForecastByMonth = async (
  month: string
): Promise<MonthlyForecast | null> => {
  const { data, error } = await supabase
    .from('monthly_forecasts')
    .select('*')
    .eq('month', month)
    .maybeSingle();

  if (error) {
    console.error('Error fetching forecast:', error);
    return null;
  }
  return data ? mapForecastFromDB(data) : null;
};

export const getOrCreateForecast = async (
  month: string
): Promise<MonthlyForecast> => {
  const existing = await getForecastByMonth(month);
  if (existing) return existing;

  const { data, error } = await supabase
    .from('monthly_forecasts')
    .insert({ month, income_items: [], expense_items: [] })
    .select()
    .single();

  if (error || !data) {
    console.error('Error creating forecast:', error);
    // Return a local empty forecast if DB fails
    return {
      id: '',
      month,
      incomeItems: [],
      expenseItems: [],
      notes: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
  return mapForecastFromDB(data);
};

export const upsertForecast = async (
  forecast: MonthlyForecast
): Promise<MonthlyForecast | null> => {
  const payload = {
    month: forecast.month,
    income_items: forecast.incomeItems,
    expense_items: forecast.expenseItems,
    notes: forecast.notes ?? null,
    updated_at: new Date().toISOString(),
  };

  if (forecast.id) {
    const { data, error } = await supabase
      .from('monthly_forecasts')
      .update(payload)
      .eq('id', forecast.id)
      .select()
      .single();
    if (error) { console.error('Error updating forecast:', error); return null; }
    return mapForecastFromDB(data);
  }

  const { data, error } = await supabase
    .from('monthly_forecasts')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('Error inserting forecast:', error); return null; }
  return mapForecastFromDB(data);
};

// ─── SUMMARY ─────────────────────────────────────────────────────────────────

export interface ForecastSummary {
  expectedIncome: number;
  expectedExpenses: number;
  projectedBalance: number;
  actualExpenses: number;
  actualIncome: number;         // no income table yet — 0
  actualBalance: number;
  savingsRate: number;          // (projectedBalance / expectedIncome) * 100
  actualSavingsRate: number;
  categoryActuals: Record<string, number>;
  categoryPlanned: Record<string, number>;
}

export const computeForecastSummary = (
  forecast: MonthlyForecast,
  allExpenses: Expense[],
  subscriptions: Subscription[]
): ForecastSummary => {
  const { month, incomeItems, expenseItems } = forecast;

  // ── Planned totals ────────────────────────────────────────────────────────
  const expectedIncome = incomeItems.reduce(
    (s, i) => s + effectiveMonthlyAmount(i.expectedAmount, i.frequency),
    0
  );
  const expectedExpenses = expenseItems.reduce(
    (s, e) => s + effectiveMonthlyAmount(e.expectedAmount, e.frequency),
    0
  );
  const projectedBalance = expectedIncome - expectedExpenses;
  const savingsRate =
    expectedIncome > 0 ? (projectedBalance / expectedIncome) * 100 : 0;

  // ── Actual spending from expenses table ────────────────────────────────────
  const monthExpenses = allExpenses.filter((e) => e.date.startsWith(month));
  const actualSpending = monthExpenses.reduce((s, e) => s + e.amount, 0);

  // ── Actual subscriptions billed this month ─────────────────────────────────
  const activeSubsTotal = subscriptions
    .filter((s) => s.status === 'active' || s.status === 'trial')
    .reduce((s, sub) => s + toMonthlyAmount(sub.amount, sub.billingCycle), 0);

  const actualExpenses = actualSpending + activeSubsTotal;
  const actualIncome = 0; // no income log table yet
  const actualBalance = actualIncome - actualExpenses;
  const actualSavingsRate =
    expectedIncome > 0
      ? ((expectedIncome - actualExpenses) / expectedIncome) * 100
      : 0;

  // ── Per-category actuals ──────────────────────────────────────────────────
  const categoryActuals: Record<string, number> = {};
  for (const e of monthExpenses) {
    categoryActuals[e.category] = (categoryActuals[e.category] ?? 0) + e.amount;
  }
  categoryActuals['subscriptions'] =
    (categoryActuals['subscriptions'] ?? 0) + activeSubsTotal;

  // ── Per-category planned ──────────────────────────────────────────────────
  const categoryPlanned: Record<string, number> = {};
  for (const e of expenseItems) {
    categoryPlanned[e.category] =
      (categoryPlanned[e.category] ?? 0) +
      effectiveMonthlyAmount(e.expectedAmount, e.frequency);
  }

  return {
    expectedIncome,
    expectedExpenses,
    projectedBalance,
    actualExpenses,
    actualIncome,
    actualBalance,
    savingsRate,
    actualSavingsRate,
    categoryActuals,
    categoryPlanned,
  };
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const effectiveMonthlyAmount = (
  amount: number,
  freq: string
): number => {
  switch (freq) {
    case 'weekly':     return amount * 4.33;
    case 'bi_weekly':  return amount * 2.165;
    case 'monthly':
    case 'once':
    default:           return amount;
  }
};

export const generateItemId = (): string =>
  `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// ─── DB MAPPER ────────────────────────────────────────────────────────────────

const mapForecastFromDB = (row: any): MonthlyForecast => ({
  id: row.id,
  month: row.month,
  incomeItems: Array.isArray(row.income_items) ? row.income_items : [],
  expenseItems: Array.isArray(row.expense_items) ? row.expense_items : [],
  notes: row.notes ?? '',
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
